import { useEffect } from 'react';
import { useStore } from './store';
import { useNav, nav, type Tab, type Screen } from './lib/nav';
import { useToast } from './lib/toast';
import { useTelegramBack } from './lib/telegram';
import { setNoVoiceHandler } from './lib/speech';
import { Ic } from './ui/Icon';
import { NoVoiceSheet, useSheet } from './ui/Sheet';
import { Onboarding } from './screens/Onboarding';
import { Gate } from './screens/Gate';
import { hasToken } from './lib/api';
import { syncStatus } from './lib/sync';
import { Home } from './screens/Home';
import { Rank } from './screens/Rank';
import { Sets } from './screens/Sets';
import { SetEditor } from './screens/SetEditor';
import { Games } from './screens/games/Games';
import { Picker } from './screens/games/Picker';
import { Arcade, ArcadeEnd } from './screens/games/Arcade';
import { Profile } from './screens/Profile';
import { Settings } from './screens/Settings';
import { Max, MaxDetail, Tariffs } from './screens/Max';
import { Lesson } from './screens/lesson/Lesson';
import { Finish } from './screens/lesson/Finish';
import { Duel } from './screens/duel/Duel';
import { Snake } from './screens/games/Snake';
import { Numbers } from './screens/games/Numbers';
import { Forms } from './screens/forms/Forms';
import { AvatarEditor } from './screens/AvatarEditor';
import { getStartParam } from './lib/telegram';
import { DonateSheet } from './ui/Support';

const DONATE_KEY = 'lexo.donateShown';

const TABS: Array<[Tab, string, string]> = [
  // 'max' (Kutubxona) tabi MVP'da yo'q — Max.tsx/MaxDetail/Tariffs ekranlar kodda qoldi
  ['home', 'home', 'Bosh sahifa'], ['sets', 'layers', 'Toʻplamlar'], ['games', 'gamepad', 'Oʻyinlar'], ['profile', 'user', 'Profil'],
];

let startHandled = false;

export function App() {
  const onb = useStore((s) => s.onb);
  const { tab, stack } = useNav();
  const top = stack[stack.length - 1];
  const sheet = useSheet();

  // Telegram BackButton: stek bo'sh bo'lmasa ko'rinadi va pop qiladi (dars ichida — o'zining «×» mantiqi)
  useEffect(() => {
    const inSession = top?.k === 'lesson' || top?.k === 'arcade' || top?.k === 'duel' || top?.k === 'snake' || top?.k === 'numbers' || top?.k === 'forms';
    return useTelegramBack(top && !inSession ? () => nav.pop() : null);
  }, [top]);

  useEffect(() => { setNoVoiceHandler(() => sheet.open(<NoVoiceSheet onClose={sheet.close} />)); }, []); // eslint-disable-line

  // Do'st jangiga taklif havolasi: t.me/<bot>?startapp=duel_<KOD> → onboarding'dan keyin jang ochiladi
  useEffect(() => {
    const sp = getStartParam();
    if (onb && hasToken() && sp?.startsWith('duel_') && !startHandled) { startHandled = true; nav.home('games'); nav.push({ k: 'duel', code: sp.slice(5) }); }
  }, [onb]);

  // Donat eslatmasi: har kirishda (sahifa yuklanishida) bir marta, asosiy tablarda
  useEffect(() => {
    if (!onb || !hasToken() || top) return;
    try { if (sessionStorage.getItem(DONATE_KEY)) return; } catch { return; }
    const id = setTimeout(() => {
      try { sessionStorage.setItem(DONATE_KEY, '1'); } catch { /* ignore */ }
      sheet.open(<DonateSheet onClose={sheet.close} />);
    }, 1500);
    return () => clearTimeout(id);
  }, [onb, !!top]); // eslint-disable-line

  let body: React.ReactNode;
  // Server sessiyasi yo'q — onboarding'ga o'tilmaydi (aks holda ism/so'rovnoma faqat localStorage'da qolib, har ochganda takrorlanadi)
  if (!hasToken()) body = <Gate kind={syncStatus() === 'unauth' ? 'telegram' : 'offline'} />;
  else if (!onb) body = <Onboarding />;
  else if (top) body = <Overlay s={top} />;
  else body = <Tabs tab={tab} />;

  return (
    <div className="app">
      {body}
      <Toast />
      {sheet.node}
    </div>
  );
}

function Tabs({ tab }: { tab: Tab }) {
  const Scr = { home: Home, sets: Sets, games: Games, max: Max, profile: Profile }[tab] ?? Home;
  return (
    <>
      <main className="content fade" id="scr" key={tab}><Scr /></main>
      <nav className="tabs">
        {TABS.map(([k, i, l]) => (
          <button key={k} className="tab" aria-current={tab === k} onClick={() => nav.tab(k)}>
            <span className="pillx"><Ic n={i} /></span>{l}
          </button>
        ))}
      </nav>
    </>
  );
}

function Overlay({ s }: { s: Screen }) {
  switch (s.k) {
    case 'rank': return <Rank />;
    case 'settings': return <Settings />;
    case 'setEditor': return <SetEditor id={s.id} />;
    case 'picker': return <Picker game={s.game} />;
    case 'maxDetail': return <MaxDetail k={s.key} from={s.from} />;
    case 'tariffs': return <Tariffs />;
    case 'lesson': return <Lesson />;
    case 'finish': return <Finish />;
    case 'arcade': return <Arcade game={s.game} pool={s.pool} />;
    case 'arcadeEnd': return <ArcadeEnd {...s} />;
    case 'duel': return <Duel code={s.code} />;
    case 'snake': return <Snake pool={s.pool} />;
    case 'numbers': return <Numbers />;
    case 'forms': return <Forms from={s.from} />;
    case 'avatar': return <AvatarEditor />;
  }
}

function Toast() {
  const msg = useToast((t) => t.msg);
  return <div className="toast" role="status" hidden={!msg}>{msg}</div>;
}
